import * as yup from "yup";

export enum NgoInputs {
  NGO_IMAGE = "logo",
  NAME = "name",
  EMAIL = "email",
  TAGLINE = "tagline",
  SUMMARY = "summary",
  ABOUT = "about",
  CATEGORY = "category",
  CITIES = "cities",
  DARPAN_ID = "darpanId",
  WEBSITE = "websiteUrl",
  EIGHTY_G_CERTIFICATE = "eightyGCertificate",
  TRUST_DEED_CERTIFICATE = "trustDeedCertificate",
  CONTACT_PERSON_NAME = "contactPerson.name",
  CONTACT_PERSON_EMAIL = "contactPerson.email",
  CONTACT_PERSON_NUMBER = "contactPerson.number",
  BANK_NAME = "bankDetails.bankName",
  BANK_BRANCH = "bankDetails.branch",
  IFSC_CODE = "bankDetails.ifscCode",
  ACCOUNT_TYPE = "bankDetails.accountType",
  BENEFICIARY_NAME = "bankDetails.beneficiaryName",
  ACCOUNT_NUMBER = "bankDetails.accountNumber",
  UPI_ID = "bankDetails.upiId",
  DONATION_QR = "donationQr",
}

export enum BankAccountType {
  SAVINGS = "savings",
  CURRENT = "current",
}

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: BankAccountType.SAVINGS, label: "Savings" },
  { value: BankAccountType.CURRENT, label: "Current" },
];

export type NgoInputKey = keyof typeof NgoInputs;
export type NgoInputValue = (typeof NgoInputs)[keyof typeof NgoInputs];

const bankDetailsSchema = (isEdit: boolean) =>
  yup.object({
    bankName: isEdit
      ? yup.string().optional()
      : yup.string().required("Bank name is required"),

    branch: isEdit
      ? yup.string().optional()
      : yup.string().required("Branch name is required"),

    ifscCode: isEdit
      ? yup.string().optional()
      : yup
        .string()
        .required("IFSC code is required"),

    accountType: isEdit
      ? yup.mixed<BankAccountType>().optional()
      : yup
        .mixed<BankAccountType>()
        .oneOf([BankAccountType.SAVINGS, BankAccountType.CURRENT])
        .required("Account type is required"),

    beneficiaryName: isEdit
      ? yup.string().optional()
      : yup.string().required("Beneficiary name is required"),

    accountNumber: isEdit
      ? yup.string().optional()
      : yup
        .string()
        .required("Account number is required")
        .matches(/^\d{9,18}$/, "Account number should be 9-18 digits"),

    upiId: yup.string().nullable().optional(),
  });

export const ngoSchema = (isEdit = false) =>
  yup.object({
    name: yup.string().required("NGO name is required"),
    email: yup.string().email("Invalid email").optional(),

    // Contact person: required only on create, optional on edit
    contact_person: yup
      .object({
        name: isEdit
          ? yup.string().optional()
          : yup.string().required("Contact person name is required"),

        email: isEdit
          ? yup.string().email("Invalid email").optional()
          : yup
            .string()
            .email("Invalid email")
            .required("Contact person email is required"),

        number: yup.string().optional(),
      })
      .required(),

    tagline: yup.string().optional(),
    summary: yup.string().optional(),
    about: yup.string().optional(),
    category: yup.string().required("Category is required"),
    cities: yup
      .array()
      .of(yup.string().required())
      .min(1, "Select at least one city")
      .required("Select at least one city"),

    darpanId: yup.string().required("Darpan ID is required"),
    websiteUrl: yup.string().url("Invalid URL").optional(),
    logo: isEdit
      ? yup.mixed<File>().nullable().optional()
      : yup.mixed<File>().nullable().required("Logo is required"),
    donationQr: isEdit
      ? yup.mixed<File>().nullable().optional()
      : yup.mixed<File>().nullable().required("Payment QR is required"),
    eightyGCertificate: isEdit
      ? yup.mixed<File>().nullable().optional()
      : yup.mixed<File>().nullable().required("80G Certificate is required"),
    trustDeedCertificate: isEdit
      ? yup.mixed<File>().nullable().optional()
      : yup
        .mixed<File>()
        .nullable()
        .required("Trust Deed Certificate is required"),
    bankDetails: bankDetailsSchema(isEdit),
  });

export interface INGOForm {
  name: string;
  email?: string;
  contact_person: {
    name?: string;
    email?: string;
    number?: string;
  };
  tagline?: string;
  summary?: string;
  about?: string;
  category: string;
  cities: string[];
  darpanId: string;
  websiteUrl?: string;
  logo?: File | null;
  donationQr?: File | null;
  eightyGCertificate?: File | null;
  trustDeedCertificate?: File | null;
  bankDetails: {
    bankName?: string;
    branch?: string;
    ifscCode?: string;
    accountType?: BankAccountType;
    beneficiaryName?: string;
    accountNumber?: string;
    upiId?: string | null;
  };
}

export interface IProject {
  _id: string;
  name: string;
  cause?: string;
  goal: number;
  startDate: string;
  endDate: string;
  isDonationEnabled: boolean;
  ngo: {
    _id: string;
    name: string;
  };
  isActive?: boolean;
}
